import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X, Scissors, CalendarCheck, LogOut } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/barbers", label: "Barbers" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("/");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  // Scroll-spy: highlight nav item for the section currently in view (homepage only)
  useEffect(() => {
    if (!isHome) return;
    const map = { home: "/", services: "/services", barbers: "/barbers", reviews: "/reviews", contact: "/contact" };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && map[e.target.id]) setActiveSection(map[e.target.id]);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    const observed = [];
    const attach = () => {
      Object.keys(map).forEach((id) => {
        const el = document.getElementById(id);
        if (el) { observer.observe(el); observed.push(el); }
      });
    };
    const t = setTimeout(attach, 300);
    return () => { clearTimeout(t); observer.disconnect(); };
  }, [isHome, location.pathname]);

  const current = isHome ? activeSection : location.pathname;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl backdrop-saturate-150 bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="w-10 h-10 rounded-full border border-[#D4AF37]/50 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
            <Scissors className="w-5 h-5 text-[#D4AF37] group-hover:text-black transition-colors duration-300" />
          </span>
          <div className="leading-none">
            <div className="font-display text-xl text-white tracking-tight">Who's Next</div>
            <div className="label-caps text-[0.55rem]">Barber Shop</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors duration-200 hover:text-[#D4AF37] ${
                current === n.to ? "text-[#D4AF37]" : "text-gray-300"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user === null ? null : user ? (
            <>
              <Link
                to="/my-bookings"
                data-testid="nav-my-bookings"
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#D4AF37] transition-colors duration-200"
              >
                <CalendarCheck className="w-4 h-4" /> My Bookings
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="nav-logout"
                className="text-gray-500 hover:text-white transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-sm text-gray-300 hover:text-[#D4AF37] transition-colors duration-200">Sign In</Link>
          )}
          <Link
            to="/book"
            data-testid="nav-book"
            className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            Book Now
          </Link>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-5 py-6 space-y-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="block text-gray-200 text-lg py-1"
              data-testid={`nav-mobile-${n.label.toLowerCase()}`}
            >
              {n.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-white/10 flex flex-col gap-3">
            {user ? (
              <>
                <Link to="/my-bookings" data-testid="nav-mobile-my-bookings" className="text-gray-200">My Bookings</Link>
                <button onClick={() => { logout(); navigate("/"); }} className="text-left text-gray-400">Sign Out</button>
              </>
            ) : (
              <Link to="/login" className="text-gray-200">Sign In</Link>
            )}
            <Link to="/book" className="px-5 py-3 rounded-full bg-[#D4AF37] text-black text-center font-medium">Book Now</Link>
          </div>
        </div>
      )}
    </header>
  );
}
